import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Shield, CheckCircle2 } from "lucide-react";

const Field = ({ label, error, children }) => (
  <div>
    <label className="text-xs font-semibold text-stone-300 mb-1 block">{label}</label>
    {children}
    {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
  </div>
);

const fmtPrice = (a, c) => a == null ? "" : c === "NGN" ? `\u20A6${a.toLocaleString()}` : `${c} ${a.toLocaleString()}`;
const inputCls = "rounded-lg bg-stone-700 border-stone-600 text-white placeholder:text-stone-400";

// Programme registration + Paystack checkout, used on the programme page and in the booking pop-up
export const RegistrationForm = ({ idPrefix = "reg" }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", preferred_channel: "email", privacy_consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => { fetch(`${API}/programme/info`).then(r => r.ok ? r.json() : null).then(setInfo).catch(() => {}); }, []);
  useEffect(() => { if (user) setForm(f => ({ ...f, name: user.name || "", email: user.email || "", phone: user.phone || "" })); }, [user]);

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = "Please enter your name";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email";
    if (!form.phone.trim() || form.phone.replace(/[\s\-()]/g, "").length < 7) e.phone = "Please enter a valid phone number";
    if (!form.privacy_consent) e.privacy_consent = "You must accept the privacy policy";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleRegister = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/programme/register`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, callback_url: `${window.location.origin}/programme` }),
      });
      if (r.ok) {
        const d = await r.json();
        if (d.authorization_url) window.location.href = d.authorization_url;
        else toast.error("Could not start payment. Please try again.");
      } else {
        const e = await r.json();
        if (e.detail?.includes("already enrolled")) { toast.info("You are already enrolled. Please use the booking link in your welcome email."); setTimeout(() => navigate("/consultation?type=initial"), 1500); }
        else toast.error(e.detail || "Registration failed");
      }
    } catch { toast.error("Connection error. Please try again."); }
    finally { setSubmitting(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-5">
      <div className="space-y-4">
        <Field label="Full name *" error={errors.name}><Input value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} data-testid={`${idPrefix}-name`} /></Field>
        <Field label="Email address *" error={errors.email}><Input type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid={`${idPrefix}-email`} /></Field>
        <Field label="Phone number *" error={errors.phone}><Input type="tel" value={form.phone} onChange={set("phone")} placeholder="+234..." className={inputCls} data-testid={`${idPrefix}-phone`} /></Field>
        <Field label="Location (optional)"><Input value={form.location} onChange={set("location")} placeholder="City, Country" className={inputCls} data-testid={`${idPrefix}-location`} /></Field>
        <Field label="Preferred communication channel">
          <Select value={form.preferred_channel} onValueChange={v => setForm({ ...form, preferred_channel: v })}>
            <SelectTrigger className="rounded-lg bg-stone-700 border-stone-600 text-white" data-testid={`${idPrefix}-channel`}><SelectValue /></SelectTrigger>
            <SelectContent className="z-[100]">
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <div className="flex items-start gap-3 pt-2">
          <input type="checkbox" id={`${idPrefix}-consent`} checked={form.privacy_consent} onChange={e => setForm({ ...form, privacy_consent: e.target.checked })}
            className="mt-1 rounded border-stone-500 text-[#FF7A00] focus:ring-[#FF7A00] bg-stone-700" data-testid={`${idPrefix}-consent`} />
          <label htmlFor={`${idPrefix}-consent`} className="text-xs text-stone-400 leading-relaxed">
            I agree to the PlateMind privacy policy and consent to my information being used for clinical care and programme communication.
          </label>
        </div>
        {errors.privacy_consent && <p className="text-xs text-red-400">{errors.privacy_consent}</p>}
      </div>
      <div className="pt-2 space-y-3">
        <Button onClick={handleRegister} disabled={submitting} className="w-full bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-xl h-12 text-base font-semibold" data-testid={`${idPrefix}-submit`}>
          {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
          Pay {fmtPrice(info?.price, info?.currency)} and start
        </Button>
        <div className="flex items-center justify-center gap-4 text-xs text-stone-500">
          <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Secure checkout</span>
          <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Paystack verified</span>
        </div>
      </div>
    </div>
  );
};
